import { CSSProperties, Key, useMemo, useState } from 'react'
import Styles from './BrandKitFonts.module.scss'
import { FONT_PAIRINGS } from 'constants/fontPairings'
import { compact, uniqBy } from 'lodash'
import { Item, Section } from '@react-stately/collections'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { CollectionElement } from '@react-types/shared'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { BrandKitFontSelectorProps, FontItemProps } from './Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Button } from 'blaze-ui/Button'
import { useRootStore } from 'stores/RootStore'
import { BrandKitFontSelectorDrawer } from './BrandKitFontSelectorDrawer'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'

export const FontItem = ({ font, fontSize, popover }: FontItemProps) => {
  return (
    <>
      <div
        style={{
          fontSize,
          fontFamily: font?.style.font_family,
          fontWeight: font?.style.font_weight,
          fontStyle: font?.style.italic ? 'italic' : 'normal',
        }}
      >
        {font?.style.font_family}
      </div>
      {font?.style.font_family && (
        <>
          <GoogleFontLoader family={font?.style.font_family} root={popover} />
          <BlazeFontLoader name={font?.style.font_family} root={popover} />
        </>
      )}
    </>
  )
}

export const BrandKitFontSelector = ({
  fontPairingState,
  selectedFontStyle,
  setSelectedFontStyle,
  fontRole,
  label,
  fontSize,
  isReadOnly,
}: BrandKitFontSelectorProps) => {
  const [popover, setPopover] = useState<HTMLDivElement>()
  const [inputLabel, setInputLabel] = useState(label)
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()

  const kitFonts = useMemo(() => {
    return compact([fontPairingState?.websitePairing?.find((font) => font.font_role === fontRole)])
  }, [fontRole, fontPairingState])

  const letterSpacing = useMemo(() => {
    return Math.max(selectedFontStyle?.style.letter_spacing || 0, 0.5)
  }, [selectedFontStyle])

  const googleFonts = useMemo(() => {
    return uniqBy(
      FONT_PAIRINGS.map((pairing) => pairing.find((font) => font.font_role === fontRole)),
      'style.font_family',
    ).filter(
      (font) => font?.style.font_family && !kitFonts.map((f) => f?.style.font_family).includes(font?.style.font_family),
    )
  }, [fontRole, kitFonts])

  const onFontSelectionChange = (key: Key | null) => {
    if (!key) return

    const [fontFamily, fontWeight] = (key as string).split('--')
    const selectedFontPairingStyle = [...kitFonts, ...googleFonts].find(
      (font) => font?.style.font_family === fontFamily && font?.style.font_weight === Number(fontWeight),
    )
    if (selectedFontPairingStyle) {
      setSelectedFontStyle(selectedFontPairingStyle)
      setInputLabel(selectedFontPairingStyle.style.font_family)
    }
  }

  if (isMobile) {
    return (
      <Button
        variant="secondary"
        size="lg"
        fullWidth
        className={Styles.comboboxInput}
        withChevron="down"
        onPress={() => {
          drawersStore.openDrawer(BrandKitFontSelectorDrawer, {
            kitFonts,
            googleFonts,
            selectedFontStyle,
            onFontSelectionChange,
          })
        }}
      >
        <span
          style={
            {
              fontFamily: selectedFontStyle?.style.font_family,
              fontWeight: selectedFontStyle?.style.font_weight,
              lineHeight: selectedFontStyle?.style.line_height,
              letterSpacing: letterSpacing,
            } as CSSProperties
          }
        >
          {inputLabel}
        </span>
      </Button>
    )
  }

  if (isReadOnly) {
    return (
      <div
        className={Styles.fontPreview}
        style={
          {
            '--font-family': selectedFontStyle?.style.font_family,
            '--font-weight': selectedFontStyle?.style.font_weight,
            '--line-height': selectedFontStyle?.style.line_height,
            '--letter-spacing': letterSpacing,
            '--font-size': `${fontSize}px`,
          } as CSSProperties
        }
      >
        {inputLabel}
      </div>
    )
  }

  return (
    <div
      className={Styles.fontSelector}
      style={
        {
          '--font-family': selectedFontStyle?.style.font_family,
          '--font-weight': selectedFontStyle?.style.font_weight,
          '--line-height': selectedFontStyle?.style.line_height,
          '--letter-spacing': letterSpacing,
          '--font-size': `${fontSize}px`,
        } as CSSProperties
      }
    >
      <ComboBox
        popoverRef={setPopover}
        popoverClassName={Styles.comboboxPopover}
        inputClassName={Styles.comboboxInput}
        contentsClassName={Styles.comboboxContents}
        size="xxl"
        aria-label={label}
        placeholder={label}
        defaultInputValue={inputLabel}
        selectedKey={`${selectedFontStyle?.style.font_family as Key}--${selectedFontStyle?.style.font_weight}`}
        menuTrigger="focus"
        autoUpdateOptions={{ animationFrame: false }}
        onSelectionChange={onFontSelectionChange}
        isDisabled={isReadOnly}
      >
        {
          [
            kitFonts.length > 0 && (
              <Section key="From your website" title={<div className={Styles.sectionHeader}>From your website</div>}>
                {[
                  ...kitFonts.map((font) => (
                    <Item
                      key={`${font?.style.font_family}--${font?.style.font_weight}`}
                      textValue={font?.style.font_family}
                    >
                      <FontItem font={font} fontSize={fontSize} popover={popover} />
                    </Item>
                  )),
                ]}
              </Section>
            ),
            <Section key="Google fonts" title={<div className={Styles.sectionHeader}>Google fonts</div>}>
              {[
                ...googleFonts.map((font) => (
                  <Item
                    key={`${font?.style.font_family}--${font?.style.font_weight}`}
                    textValue={font?.style.font_family}
                  >
                    <FontItem font={font} fontSize={fontSize} popover={popover} />
                  </Item>
                )),
              ]}
            </Section>,
          ].filter(Boolean) as CollectionElement<HTMLElement>[]
        }
      </ComboBox>
      {selectedFontStyle?.style.font_family && <GoogleFontLoader family={selectedFontStyle.style.font_family} />}
    </div>
  )
}
